/**
 * Admin V3 — Drivers — Compliance History Panel (Plan §2.2 M-F-9).
 * Driver-scoped view of driver_compliance_events.
 * GET /admin/compliance/audit?driver_id=<id>
 */
import React from 'react';
import { History } from 'lucide-react';
import { SectionCard, EmptyState } from '../../shared/visualTokens';
import useAdminQuery from '../../shared/hooks/useAdminQuery';
import { t } from '../../i18n';

const DriverComplianceHistoryPanel = ({ driverId, locale = 'fr' }) => {
  const { data, loading, error } = useAdminQuery(
    '/admin/compliance/audit',
    driverId ? { driver_id: driverId, limit: 100 } : { limit: 0 },
  );
  const events = (data && data.events) || [];

  return (
    <SectionCard
      icon={History}
      iconClass="bg-violet-100 text-violet-700"
      title={t('compliance.history.title', locale)}
    >
      {loading && <p className="text-slate-500 text-sm">{t('compliance.common.loading', locale)}</p>}
      {error && <p className="text-rose-700 text-sm">{t('compliance.common.error', locale)}</p>}
      {!loading && !error && events.length === 0 && (
        <EmptyState icon={History} title={t('compliance.history.empty', locale)} />
      )}
      {!loading && !error && events.length > 0 && (
        <div className="overflow-x-auto" data-testid="driver-compliance-history">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-slate-600">
                <th className="py-2 pr-4">{t('compliance.activity.table.timestamp', locale)}</th>
                <th className="py-2 pr-4">{t('compliance.activity.table.event', locale)}</th>
                <th className="py-2 pr-4">{t('compliance.activity.table.actor', locale)}</th>
                <th className="py-2 pr-4">{t('compliance.activity.table.transition', locale)}</th>
                <th className="py-2 pr-4">{t('compliance.activity.table.reason', locale)}</th>
              </tr>
            </thead>
            <tbody>
              {events.map((e) => (
                <tr key={e.id} className="border-b border-slate-100">
                  <td className="py-2 pr-4 text-slate-700">{e.timestamp || '—'}</td>
                  <td className="py-2 pr-4 text-slate-900">{e.event_type}</td>
                  <td className="py-2 pr-4 text-slate-700">{e.actor_id} ({e.actor_role})</td>
                  <td className="py-2 pr-4 text-slate-700">
                    {e.previous_state || '—'} → {e.new_state || '—'}
                  </td>
                  <td className="py-2 pr-4 text-slate-600">{e.reason || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
};

export default DriverComplianceHistoryPanel;
